import { useEffect, useState, useRef } from "react";
import d3Tip from "d3-tip";
import { withTranslation } from "react-i18next";
import CodeUtil from "@plannel/pages/utils/code-util";
import { FONT_FAMILY_CSS } from "@plannel/utils/font-util";
import { cloneDeep, entries, isNull } from "lodash";

const d3 = require("d3");

const AbcHeatMap = props => {
  const { t, name, size, yAxisName, mapData, selectHeatMap, maxCv } = props;

  const { gradeA, gradeB } = props;
  const interval = 0.05;
  const gradeX = props.gradeX.toFixed(2).toString();
  const gradeY = props.gradeY.toFixed(2).toString() === maxCv ? (maxCv - interval).toString() : props.gradeY.toFixed(2).toString();
  const gradeZ = (maxCv - interval).toFixed(2).toString();
  const abcConfig = CodeUtil.ABCClassConfig;
  const [node, setNode] = useState();
  const [load, setLoad] = useState(false);
  const [width, setWidth] = useState();
  const [height, setHeight] = useState();

  const [gXScale, setGXScale] = useState(null);
  const [gYScale, setGYScale] = useState(null);
  const [gSvg, setGSvg] = useState();

  const heatMapRef = useRef();
  const [observer] = useState(
    new ResizeObserver((obsEntries, _observer) => {
      for (const entry of obsEntries) {
        const { width: rectWidth, height: rectHeight } = entry.contentRect;
        setWidth(rectWidth);
        setHeight(rectHeight);
      }
    })
  );

  const removeTips = () => {
    const tipEls = document.getElementsByClassName("d3-tip");
    if (tipEls?.length > 0) {
      for (const tipEl of tipEls) {
        tipEl.remove();
      }
    }
  };

  const margin = { top: 40, left: 50, right: 25, bottom: 40 };

  const drawRect = (pSvg, pXScale, pYScale) => {
    const drawSvg = pSvg ?? gSvg;
    const drawXScale = pXScale ?? gXScale;
    const drawYScale = pYScale ?? gYScale;

    if (!drawSvg || !drawXScale || !drawYScale) return false;

    const grade = ["A", "B", "C"];
    const cv = ["X", "Y", "Z"];
    const padding = 3;
    const cv_min = "0.00";
    const gradeC = 95;

    const threshold = {
      A: [drawYScale(gradeA), drawYScale(0)],
      B: [drawYScale(gradeB), gradeA < 95 ? drawYScale(gradeA) : drawYScale(95)],
      C: [drawYScale(gradeC) + drawYScale.bandwidth(), drawYScale(gradeB)],
      X: [drawXScale(gradeX), drawXScale(cv_min)],
      Y: [
        parseFloat(gradeZ) < maxCv ? drawXScale(gradeY) : drawXScale(gradeY) + drawXScale.bandwidth(),
        drawXScale(gradeX),
      ],
      Z: [drawXScale(gradeZ) + drawXScale.bandwidth(), drawXScale(gradeY)],
    };

    const rectColor = {
      AX: abcConfig.AX.style.backgroundColor,
      BX: abcConfig.BX.style.backgroundColor,
      CX: abcConfig.CX.style.backgroundColor, 
      AY: abcConfig.AY.style.backgroundColor,
      BY: abcConfig.BY.style.backgroundColor,
      CY: abcConfig.CY.style.backgroundColor,
      AZ: abcConfig.AZ.style.backgroundColor,
      BZ: abcConfig.BZ.style.backgroundColor,
      CZ: abcConfig.CZ.style.backgroundColor,
    };

    if (gradeA < 5) {
      grade.splice(grade.indexOf("A"), 1);
    }
    if (gradeA === gradeB) {
      grade.splice(grade.indexOf("B"), 1);
    }
    if (gradeB > 100) {
      grade.splice(grade.indexOf("C"), 1);
    }
    if (parseFloat(gradeX) < interval) {
      cv.splice(cv.indexOf("X"), 1);
    }
    if (parseFloat(gradeX) === parseFloat(gradeY)) {
      cv.splice(cv.indexOf("Y"), 1);
    }
    if (parseFloat(gradeZ) >= maxCv) {
      cv.splice(cv.indexOf("Z"), 1);
    }

    grade.forEach(g => {
      cv.forEach(c => {
        if (threshold[c][0] >= 0 && threshold[c][1] >= 0 && threshold[g][0] >= 0 && threshold[g][1] >= 0) {
          drawSvg
          .append("rect")
          .style("stroke", rectColor[g + c])
          .style("stroke-width", 2)
          .style("fill-opacity", 0)
          .attr("id", "abcRect")
          .attr("pointer-events", "none")
          .attr("x", threshold[c][1] + margin.left + padding)
          .attr("y", threshold[g][1] + margin.top + padding)
          .attr("width", threshold[c][0] - threshold[c][1] - padding)
          .attr("height", threshold[g][0] - threshold[g][1] - padding);
        drawSvg
          .append("text")
          .attr("x", (threshold[c][0] + threshold[c][1] - drawXScale.bandwidth()) / 2 + margin.left)
          .attr("y", (threshold[g][0] + threshold[g][1] + drawYScale.bandwidth()) / 2 + margin.top)
          .attr("font-size", "15px")
          .attr("id", "abcRect")
          .attr("pointer-events", "none")
          .text(g + c);
        }
      });
    });
    return null;
  };

  const highlight = (e, xScale, yScale) => {
    d3.select(e.target)
      // .select(d3.event.target)
      .style("stroke", "gray")
      .style("fill-opacity", 1)
      // shrink a bit to make room for stroke, now visible
      .attr("x", d => xScale(d.cv_lbl) + margin.left + 1)
      .attr("y", d => yScale(d.pct) + margin.top + 1)
      .attr("width", xScale.bandwidth() - 2)
      .attr("height", yScale.bandwidth() - 2);
  };

  const unhighlight = (e, xScale, yScale) => {
    d3.select(e.target)
      // .select(d3.event.target)
      .attr("x", d => xScale(d.cv_lbl) + margin.left)
      .attr("y", d => yScale(d.pct) + margin.top)
      .attr("width", xScale.bandwidth())
      .attr("height", yScale.bandwidth())
      .style("stroke", "white")
      .style("fill-opacity", 0.7);
  };

  const process_data = (grp_dat, xLabel) => {
    const ra = Array(grp_dat.length * xLabel.length);

    for (let i = 0; i < ra.length; i += xLabel.length) {
      for (let j = 0; j < xLabel.length; j++) {
        ra[i + j] = {
          pct: grp_dat[parseInt(i / xLabel.length, 10)].pct,
          cv_lbl: xLabel[j],
          sub_count: grp_dat[parseInt(i / xLabel.length, 10)][xLabel[j]],
        };
      }
    }

    return ra;
  };

  const createHeatMap = () => {
    removeTips();
    const getDefaultData = () => {
      const xLegend = [];
      const yLegend = [];

      for (let i = 0; i < 20; i++) {
        const legend = 95 - i * 5;
        yLegend.push(legend);
      }

      for (let i = 0; i < maxCv*10; i++) {
        const legend = (i / 20).toFixed(2).toString();
        xLegend.push(legend);
      }

      const dataList = [];
      for (const q of yLegend) {
        const obj = { pct: q, cnt: 0 };
        for (const [idx, c] of entries(xLegend)) {
          idx === "0" && q === 95 ? (obj[c] = 8) : (obj[c] = 0);
        }
        dataList.push(obj);
      }
      return dataList;
    };

    const data = mapData.length > 0 ? mapData : getDefaultData();
    if (data.length < 1) return false;
    const thisContainerWidth = document.getElementById(`heatmap${name}`)?.width?.baseVal?.value;
    const thisContainerHeight = document.getElementById(`heatmap${name}`)?.height?.baseVal?.value;

    const d3Width = width ?? thisContainerWidth;
    const d3Height = height ?? thisContainerHeight + 10;

    if (node) {
      d3.select(node).selectAll("*").remove();
    }
    const svg = d3.select(node);
    setGSvg(d3.select(node));

    let wasDragged = false;

    const x_label = Object.keys(data[0]).sort().slice(0, -2);
    const y_label = Array.from(new Set(d3.map(data, d => d.pct)));
    const right_label = [];
    const chart_dat = process_data(data, x_label);
    const right_scale = d3
      .scaleBand()
      .domain(d3.range(right_label.length))
      .range([d3Height - margin.bottom - margin.top, 0]);
    const y_scale = d3
      .scaleBand()
      .domain(y_label)
      .range([d3Height - margin.bottom - margin.top, 0]);
    setGYScale(y_scale);
    const x_scale = d3
      .scaleBand()
      .domain(x_label)
      .range([0, d3Width - margin.left - margin.right]);
    setGXScale(x_scale);
    
    const dataList = cloneDeep(chart_dat);
    dataList.sort((a, b) => b.sub_count - a.sub_count);
    const secondMax = dataList[1].sub_count === 0 ? 1 : dataList[1].sub_count;

    const color_scale = d3.scaleSequential(d3.interpolatePurples).domain([0, secondMax]);
    
    const valRange = [0, secondMax];
    const legendCnt = 10;
    const legendBins = [...Array(legendCnt).keys()].map(x => d3.quantile(valRange, x * 0.1));
    const legendHeight = 10;
    const legendElementWidth = (d3Width - margin.left - margin.right) / legendCnt;

    svg
      .append("g")
      .attr("transform", `translate(${margin.left / 3}, ${d3Height / 2})`)
      .append("text")
      .attr("transform", "rotate(270)")
      .style("text-anchor", "middle")
      .style("font-size", "15px")
      .style("font-family", FONT_FAMILY_CSS)
      .text(t(yAxisName));

    svg
      .append("g")
      .attr("transform", `translate(${d3Width / 2}, ${margin.top / 2 - 6})`)
      .append("text")
      .style("text-anchor", "center")
      .style("font-size", "15px")
      .style("font-family", FONT_FAMILY_CSS)
      .text(t("cv"));

    svg
      .append("g")
      // .attr("transform", `translate(${margin.left}, 0)`)
      .attr("transform", `translate(${margin.left + 11}, ${margin.top - 4})`)
      .call(d3.axisTop(x_scale).tickSize(0).tickPadding(7))
      .call(g => g.select(".domain").remove())
      .selectAll("text")
      // .attr("y", 7)
      .style("text-anchor", "center")
      .attr("transform", "rotate(-55)")
      .style("color", (d, i) => {
        return i % 2 === 1 ? "#777" : "white";
      });

    svg
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .call(d3.axisLeft(y_scale).tickSize(0).tickPadding(7))
      .call(g => g.select(".domain").remove())
      .selectAll("text")
      .style("fill", "#777");

    svg
      .append("g")
      .attr("transform", `translate(${d3Width - margin.right}, ${margin.top})`)
      .call(
        d3
          .axisRight(right_scale)
          .tickFormat((_d, i) => right_label[i])
          .tickSize(2)
          .tickPadding(7)
      )
      .call(g => g.select(".domain").remove())
      .selectAll("text")
      .style("fill", "#777");

    svg
      .selectAll("g")
      .data(chart_dat)
      .enter()
      .append("g")
      .attr("transform", d => `translate(0,${y_scale(d.pct) + 1})`);

    const tip = d3Tip()
      .attr("class", "d3-tip")
      .offset([3, 0])
      .direction("s")
      .style("font-family", FONT_FAMILY_CSS)
      .style("line-height", 1.2)
      .style("font-size", "15px")
      .style("background-color", d3.rgb(0, 0, 0, 0.7))
      .style("color", "white")
      .style("border", "None")
      .style("border-width", "1px")
      .style("border-radius", "2px")
      .style("padding", "7px")
      .style("stroke", "black")
      .html((_e, d) => {
        return `${t("volume")} : ${d.pct}<br>${t("cv")} : ${d.cv_lbl}<br>${t("count")} : ${d.sub_count}`;
      });
    // .html(function(d) {
    //     return `qty : ${d.pct}<br>cv : ${d.cv_lbl}<br>count : ${d.sub_count}`;
    // })
    svg.call(tip);

    let cumm_dx = 0;
    let cumm_dy = 0;
    let drag_startX = 0;
    let drag_startY = 0;
    let select_startX = 0;
    let select_endX = 0;
    let select_startY = 0;
    let select_endY = 0;

    svg
      .selectAll()
      .data(chart_dat)
      .enter()
      .append("rect")
      .attr("id", name)
      .attr("x", d => x_scale(d.cv_lbl) + margin.left)
      .attr("y", d => y_scale(d.pct) + margin.top)
      .attr("rx", 2)
      .attr("ry", 2)
      .attr("width", x_scale.bandwidth())
      .attr("height", y_scale.bandwidth())
      .attr("fill", d => color_scale(d.sub_count))
      .style("stroke", "white")
      .style("fill-opacity", 0.7)
      .on("mouseover", tip.show)
      .on("mousemove", d => {
        highlight(d, x_scale, y_scale);
      })
      .on("mouseleave", d => {
        unhighlight(d, x_scale, y_scale);
      })
      .on("mouseout", tip.hide)
      .call(
        d3
          .drag()
          .on("start", (e, _drag) => {
            cumm_dx = 0;
            cumm_dy = 0;
            drag_startX = d3.select(e.sourceEvent.target).attr("x") - 1;
            drag_startY = d3.select(e.sourceEvent.target).attr("y") - 1;

            e.sourceEvent.stopPropagation();
          })
          .on("drag", (e, _dx) => {
            d3.selectAll("#selected").remove();
            cumm_dx += e.dx;
            cumm_dy += e.dy;

            if (cumm_dx < 0) {
              select_startX = Math.max(e.x + 1, margin.left);
              select_endX = drag_startX + x_scale.bandwidth();
            } else {
              select_startX = drag_startX;
              select_endX = Math.min(e.x - 1, d3Width - margin.right);
            }

            if (cumm_dy < 0) {
              select_startY = Math.max(e.y + 1, margin.top);
              select_endY = drag_startY + y_scale.bandwidth();
            } else {
              select_startY = drag_startY;
              select_endY = Math.min(e.y - 1, d3Height - margin.bottom);
            }
            d3.select(node)
              .append("rect")
              .attr("id", "selected")
              .attr("x", select_startX)
              .attr("y", select_startY)
              .attr("fill", d3.rgb(0, 0, 10, 0.2))
              .attr("width", Math.abs(select_startX - select_endX))
              .attr("height", Math.abs(select_startY - select_endY))
              .style("stroke", "black")
              .style("stroke-width", "0.5")
              .on("click", _d => {
                d3.selectAll("#selected").remove();
              });

            wasDragged = true;
          })
          .on("end", (e, _dend) => {
            if (wasDragged === true) {
              const endrect_x = parseFloat(d3.select(e.sourceEvent.target).attr("x"));
              const endrect_y = parseFloat(d3.select(e.sourceEvent.target).attr("y"));

              if (cumm_dx < 0) {
                select_startX = Math.max(Number.isNaN(endrect_x) ? 0 : endrect_x, margin.left);
              } else {
                select_endX = Math.min(
                  Number.isNaN(endrect_x) ? d3Width - margin.right : endrect_x + x_scale.bandwidth(),
                  d3Width - margin.right
                );
              }

              if (cumm_dy < 0) {
                select_startY = Math.max(Number.isNaN(endrect_y) ? 0 : endrect_y, margin.top);
              } else {
                select_endY = Math.min(
                  Number.isNaN(endrect_y) ? d3Height - margin.bottom : endrect_y + y_scale.bandwidth(),
                  d3Height - margin.bottom
                );
              }

              d3.selectAll("#selected").remove();
              d3.select(node)
                .append("rect")
                .attr("id", "selected")
                .attr("x", select_startX)
                .attr("y", select_startY)
                .attr("fill", d3.rgb(0, 0, 10, 0.2))
                .attr("width", Math.abs(select_startX - select_endX))
                .attr("height", Math.abs(select_startY - select_endY))
                .style("stroke", "black")
                .style("stroke-width", "0.5")
                .on("click", _d => {
                  d3.selectAll("#selected").remove();
                });
              const selected = d3.select("#selected");
              const selected_x1 = parseFloat(selected.attr("x"));
              const selected_y1 = parseFloat(selected.attr("y"));
              const selected_x2 = parseFloat(selected.attr("x")) + parseFloat(selected.attr("width"));
              const selected_y2 = parseFloat(selected.attr("y")) + parseFloat(selected.attr("height"));
              const coord_to_idx = [];

              const c_x1 = Math.round((selected_x1 - margin.left) / x_scale.bandwidth());
              const c_x2 = Math.round((selected_x2 - margin.left - x_scale.bandwidth()) / x_scale.bandwidth());
              const c_y1 = Math.round((selected_y1 - margin.top) / y_scale.bandwidth());
              const c_y2 = Math.round((selected_y2 - margin.top - y_scale.bandwidth()) / y_scale.bandwidth());

              for (let i = c_y1; i <= c_y2; i++) {
                for (let j = c_x1; j <= c_x2; j++) {
                  coord_to_idx.push((y_label.length - 1 - i) * x_label.length + j);
                }
              }

              const selected_rects = d3
                .selectAll(`#${name}`)
                .filter((_d, i) => {
                  return coord_to_idx.includes(i);
                })
                .data();

              selectHeatMap(selected_rects);

              const formData = new URLSearchParams();
              selected_rects.forEach(event => {
                formData.append("selected", JSON.stringify(event));
              });
            }
            wasDragged = false;
          })
      )
      .on("click", ev => {
        if (ev.defaultPrevented === false) {
          d3.selectAll("#selected").remove();
        }
      });

    drawRect(svg, x_scale, y_scale);
    const legend = svg.append("g").attr("transform", _dlegend => `translate(${margin.left},0)`);

    legend
      .selectAll("rect")
      .data(legendBins)
      .enter()
      .append("rect")
      .attr("x", (d, i) => legendElementWidth * i)
      .attr("y", d3Height - margin.bottom + legendHeight)
      .attr("width", legendElementWidth)
      .attr("height", legendHeight)
      .style("fill", d => color_scale(d));

    legend
      .selectAll("text")
      .data(legendBins)
      .enter()
      .append("text")
      .text( d =>  {
        if (d === 0) {
            return null;
        } else {
            return ` ≥${d.toExponential(1)}`
        }
      })
      .attr("x", (d, i) => legendElementWidth * i)
      .attr("y", d3Height - margin.bottom + legendHeight * 3)
      .style("font-size", "8pt")
      .style("font-family", "Consolas, courier")
      .style("fill", "#aaa")
      .style("fill-opacity", 0.7);

    return null;
  };

  useEffect(() => {
    setLoad(true);
    if (heatMapRef.current) {
      observer.observe(heatMapRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (load) {
      createHeatMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  useEffect(() => {
    if (load && width && height && mapData.length > 0) {
      createHeatMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height, mapData]);

  useEffect(() => {
    if (!isNull(props.gradeA) && props.gradeB) {
      createHeatMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.gradeA, props.gradeB, props.gradeX, props.gradeY]);

  return (
    <svg
      ref={refNode => {
        setNode(refNode);
        heatMapRef.current = refNode;
      }}
      id={`heatmap${name}`}
      width={size[0]}
      height={size[1]}></svg>
  );
};

export default withTranslation()(AbcHeatMap);
